import { InputNumber, Modal, Slider } from 'antd';
import React, { useEffect, useState } from 'react';

interface ClockEditorProps {
    /** null = hidden. */
    mode: 'main' | 'shot' | null;
    mainSeconds: number;
    shotSeconds: number;
    /** Upper bound for the shot clock = min(28, game clock remaining). */
    shotMax: number;
    /** Upper bound for the game clock (a full period). */
    mainMax: number;
    onSubmitMain: (seconds: number) => void;
    onSubmitShot: (seconds: number) => void;
    onCancel: () => void;
}

const fmt = (total: number): string => {
    const whole = Math.max(0, Math.floor(total));
    const m = Math.floor(whole / 60);
    const s = whole - m * 60;
    return `${m}:${s < 10 ? `0${s}` : s}`;
};

const ClockEditor: React.FC<ClockEditorProps> = ({
    mode,
    mainSeconds,
    shotSeconds,
    shotMax,
    mainMax,
    onSubmitMain,
    onSubmitShot,
    onCancel,
}) => {
    const [minutes, setMinutes] = useState(0);
    const [seconds, setSeconds] = useState(0);
    const [shot, setShot] = useState(0);

    useEffect(() => {
        if (mode === 'main') {
            setMinutes(Math.floor(mainSeconds / 60));
            setSeconds(Math.floor(mainSeconds % 60));
        } else if (mode === 'shot') {
            setShot(Math.min(Math.ceil(shotSeconds), shotMax));
        }
    }, [mode, mainSeconds, shotSeconds, shotMax]);

    const handleOk = (): void => {
        if (mode === 'main') {
            onSubmitMain(Math.max(0, Math.min(mainMax, minutes * 60 + seconds)));
        } else if (mode === 'shot') {
            onSubmitShot(Math.max(0, Math.min(shotMax, shot)));
        }
    };

    const total = minutes * 60 + seconds;

    return (
        <Modal
            visible={mode !== null}
            title={mode === 'main' ? 'Set game clock' : 'Set shot clock'}
            onOk={handleOk}
            onCancel={onCancel}
            okText="Set"
            destroyOnClose
        >
            {mode === 'main' && (
                <div className="m-editor">
                    <div className="m-editor__row">
                        <InputNumber
                            min={0}
                            max={Math.floor(mainMax / 60)}
                            value={minutes}
                            onChange={(v): void => setMinutes(Number(v ?? 0))}
                        />
                        <span className="m-editor__unit">min</span>
                        <InputNumber
                            min={0}
                            max={59}
                            value={seconds}
                            onChange={(v): void => setSeconds(Number(v ?? 0))}
                        />
                        <span className="m-editor__unit">sec</span>
                    </div>
                    <Slider
                        min={0}
                        max={mainMax}
                        value={total > mainMax ? mainMax : total}
                        tipFormatter={(v): string => fmt(v ?? 0)}
                        onChange={(v): void => {
                            const next = v as number;
                            setMinutes(Math.floor(next / 60));
                            setSeconds(next % 60);
                        }}
                    />
                    <div className="m-editor__hint">{fmt(Math.min(total, mainMax))}</div>
                </div>
            )}

            {mode === 'shot' && (
                <div className="m-editor">
                    <InputNumber
                        min={0}
                        max={shotMax}
                        value={shot}
                        onChange={(v): void => setShot(Number(v ?? 0))}
                    />
                    <Slider
                        min={0}
                        max={shotMax}
                        value={shot > shotMax ? shotMax : shot}
                        onChange={(v): void => setShot(v as number)}
                    />
                    <div className="m-editor__hint">
                        Max {shotMax}s (cannot exceed the game clock).
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ClockEditor;
